// alpine/requestStore.js
export function createRequestStore({ endpoint, nonce } = {}) {
  const safeJsonParse = (raw, fallback) => {
    try {
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  const safeStorageGet = (storage, key) => {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  };

  const safeStorageSet = (storage, key, value) => {
    try {
      storage.setItem(key, value);
    } catch {}
  };

  const readCookie = (name) => {
    try {
      const safe = String(name).replace(/[^a-zA-Z0-9_-]/g, '_') + '=';
      const parts = document.cookie ? document.cookie.split(';') : [];
      for (let i = 0; i < parts.length; i++) {
        const c = parts[i].trim();
        if (c.indexOf(safe) === 0) {
          return decodeURIComponent(c.substring(safe.length));
        }
      }
    } catch {}
    return '';
  };

  const getPrefix = () => {
    const direct = window.QRSHOP?.prefix || window.QRSHOP_BASE?.prefix;
    if (direct) return direct;
    // prefix가 없을 때 특정 매장(mono)으로 편향되지 않도록 현재 shop 기반으로 유도.
    const shop = window.QRSHOP?.shop || window.QRSHOP_BASE?.shop || '';
    return shop ? `qrshop:v1:${shop}:` : 'qrshop:v1:global:';
  };

  const cartKey = () => `${getPrefix()}cart:items`;
  const ctxKey = () => `${getPrefix()}ctx`;
  const backupKey = () => `${getPrefix()}ctx:backup`;
  const sidKey = () => `${getPrefix()}sid`;

  const getCurrentShop = () =>
    window.QRSHOP?.shop || window.QRSHOP_BASE?.shop || '';

  const isValidQrContext = (ctx) => {
    if (!ctx || typeof ctx !== 'object') return false;

    const shop = typeof ctx.shop === 'string' ? ctx.shop.trim() : '';
    const src = typeof ctx.src === 'string' ? ctx.src.trim() : '';
    const loc = typeof ctx.loc === 'string' ? ctx.loc.trim() : '';
    const expiresAt = Number(ctx.expires_at || 0);
    const currentShop = getCurrentShop();

    if (currentShop && shop && shop !== currentShop) return false;
    if (src !== 'qr' || !loc) return false;
    if (!expiresAt || expiresAt <= Date.now()) return false;

    return true;
  };

  const cacheQrContext = (ctx) => {
    const loc = String(ctx?.loc || '').trim();
    if (!loc || !document.body) return;

    document.body.dataset.qrshopSrc = 'qr';
    document.body.dataset.qrshopLoc = loc;
    document.body.dataset.qrshopLocLocked = '1';
  };

  const getQrContext = () => {
    const sessionCtx = safeJsonParse(
      safeStorageGet(sessionStorage, ctxKey()),
      {}
    );

    if (isValidQrContext(sessionCtx)) {
      cacheQrContext(sessionCtx);
      return sessionCtx;
    }

    const backupCtx = safeJsonParse(
      safeStorageGet(localStorage, backupKey()),
      {}
    );

    if (isValidQrContext(backupCtx)) {
      const restoredCtx = { ...backupCtx };
      delete restoredCtx.restore_until;

      safeStorageSet(sessionStorage, ctxKey(), JSON.stringify(restoredCtx));
      if (restoredCtx.sid) {
        safeStorageSet(sessionStorage, sidKey(), String(restoredCtx.sid));
      }
      cacheQrContext(restoredCtx);

      return restoredCtx;
    }

    // 추가 복구원: 쿠키 백업(인앱 웹뷰에서 session/localStorage 유실 대비).
    // 검증은 기존 백업과 동일 규칙 적용.
    const cookieCtx = safeJsonParse(readCookie(backupKey()), {});

    if (!isValidQrContext(cookieCtx)) {
      return {};
    }

    const restoredCookie = { ...cookieCtx };
    delete restoredCookie.restore_until;

    safeStorageSet(sessionStorage, ctxKey(), JSON.stringify(restoredCookie));
    if (restoredCookie.sid) {
      safeStorageSet(sessionStorage, sidKey(), String(restoredCookie.sid));
    }
    cacheQrContext(restoredCookie);

    return restoredCookie;
  };

  const callErrorMessage =
    '요청하신 내용중에 오류가 있습니다. 가까운 직원에게 말씀해 주세요';
  const callEmptyMessage = '요청하실 내용을 간단히 입력해 주세요';

  // 전체 언어 허용. 링크(URL)와 악성 코드(HTML/스크립트)만 차단한다.
  // 이모지·문장부호·외국어 등 정상 입력은 언어와 무관하게 통과시킨다.
  const hasBlockedCallContent = (value) => {
    const text = String(value || '').trim();
    if (!text) return false;

    // 링크(URL) 차단
    if (/https?:\/\//i.test(text)) return true; // http/https 스킴
    if (/(^|[\s/])www\./i.test(text)) return true; // www. 시작
    // 스킴 없는 도메인(example.com, shop.co.kr 등)
    if (
      /\b[a-z0-9][a-z0-9-]*\.(?:com|net|org|co|kr|io|me|shop|store|app|dev|info|biz|xyz|link|page|site|online|gg|tv|cc|ly|ai)(?:[/?#]|\b)/i.test(
        text
      )
    )
      return true;

    // 악성 코드(HTML 태그/스크립트) 차단
    if (/<\s*[a-z!/][^>]*>/i.test(text)) return true; // <script>, <a ...>, <!-- 등
    if (/javascript:/i.test(text)) return true; // javascript: 스킴

    return false;
  };

  return {
    submitting: false,

    validate(type, extra = {}) {
      if (type === 'cart' || type === 'call') {
        const loc = this.getLoc(extra.loc, extra.locCharge);

        if (!loc) {
          window.Alpine?.store('toast')?.show?.(
            '고객 위치를 입력해 주세요.',
            'warning'
          );
          // 손님 화면은 그대로(경고 유지). 원인 분석용 리포트만 추가.
          this.reportError('ERR_LOC_EMPTY', type, {
            message: 'loc empty at submit',
          });
          this.submitting = false;
          return false;
        }
      }

      if (type === 'call') {
        const request = String(extra.request || '').trim();

        if (!request) {
          window.Alpine?.store('toast')?.show?.(callEmptyMessage, 'info');
          this.submitting = false;
          return false;
        }

        if (hasBlockedCallContent(request)) {
          window.Alpine?.store('toast')?.show?.(callErrorMessage, 'error');
          this.submitting = false;
          return false;
        }
      }

      if (type === 'request') {
        const request = String(extra.request || '').trim();
        if (!request) {
          window.Alpine?.store('toast')?.show?.(
            '문의내용을 입력해주세요.',
            'error'
          );
          this.submitting = false;
          return false;
        }
        if (!extra.agree) {
          window.Alpine?.store('toast')?.show?.(
            '개인정보처리방침에 동의해주세요.',
            'error'
          );
          this.submitting = false;
          return false;
        }
      }
      return true;
    },

    labelFor(type) {
      if (type === 'request') return '문의하기';
      return type === 'cart' ? '요청하기' : '호출하기';
    },
    buttonText(type) {
      return this.submitting ? this.buttonTextIng() : this.labelFor(type);
    },
    buttonTextIng() {
      return '접수중...';
    },

    getCartItems() {
      return safeJsonParse(safeStorageGet(localStorage, cartKey()), {
        d: [],
        s: [],
        p: [],
      });
    },

    getBaseLoc(inputLoc) {
      // 1) 입력값 최우선
      if (inputLoc && typeof inputLoc === 'object' && 'value' in inputLoc) {
        inputLoc = inputLoc.value;
      }

      if (typeof inputLoc === 'string' && inputLoc.trim() !== '') {
        return inputLoc.trim();
      }

      const domLoc = document.body?.dataset?.qrshopLoc || '';
      if (domLoc) return String(domLoc).trim();

      const ctx = getQrContext();
      if (ctx?.loc) return String(ctx.loc).trim();

      return '';
    },

    getLoc(inputLoc, inputCharge = '') {
      const loc = this.getBaseLoc(inputLoc);
      const charge = String(inputCharge || '').trim();

      return loc && charge ? `${loc}-${charge}` : loc;
    },

    // ctx가 유실돼도 현재 페이지 매장 ID로 shop_id를 보완(게이팅과 무관).
    resolveShopId(ctx) {
      return (
        ctx?.shop_id ||
        window.QRSHOP_BASE?.shop_id ||
        window.QRSHOP?.shop_id ||
        null
      );
    },

    buildPayload(type, extra = {}) {
      const ctx = getQrContext();
      const loc = this.getLoc(extra.loc, extra.locCharge);

      if (type === 'cart') {
        const items = this.getCartItems();
        return {
          type: 'cart',
          content: this.formatCartContent(items, loc),
          meta: { loc, items, shop_id: this.resolveShopId(ctx) },
        };
      }

      if (type === 'call') {
        const request = String(extra.request || '').trim();
        return {
          type: 'call',
          content: this.formatCallContent(request, loc),
          meta: { loc, request, shop_id: this.resolveShopId(ctx) },
        };
      }

      if (type === 'send') {
        const request = String(extra.name + extra.phone).trim();
        return {
          type: 'send',
          content: this.formatCallContent(request),
          meta: { shop_id: extra.shop_id || null },
        };
      }

      if (type === 'request') {
        const request = String(extra.request || '').trim();
        return {
          type: 'request',
          content: this.formatCallContent(request),
          meta: { request, agree: !!extra.agree },
        };
      }

      throw new Error(`Unknown type: ${type}`);
    },

    formatCartContent(items, loc) {
      const line = (label, list) =>
        Array.isArray(list) && list.length
          ? `${label}: ${list.join(', ')}`
          : null;

      const lines = [
        // loc ? `위치: ${loc}` : null,
        line('D', items.d),
        line('S', items.s),
        line('P', items.p),
      ].filter(Boolean);

      return lines.join('\n');
    },

    formatCallContent(request, loc) {
      return [
        // loc ? `위치: ${loc}` : null,
        request ? `${request}` : `-`,
      ]
        .filter(Boolean)
        .join('\n');
    },

    // HTTP 상태 + 서버 메시지 → 에러 코드
    mapHttpErrorCode(status, message) {
      const m = String(message || '');
      if (status === 400 && /post_id/i.test(m)) return 'ERR_HTTP_400_POST_ID';
      if (status === 400 && /loc/i.test(m)) return 'ERR_HTTP_400_LOC';
      if (status === 400) return 'ERR_HTTP_400';
      if (status === 403) return 'ERR_HTTP_403';
      if (status >= 500) return 'ERR_HTTP_500';
      return `ERR_HTTP_${status}`;
    },

    // QR 컨텍스트 유효성 진단 → 실패 원인 코드
    diagnoseContext() {
      const shop = getCurrentShop();

      if (shop && !getPrefix().includes(`:${shop}:`)) {
        return 'ERR_PREFIX_FALLBACK';
      }

      const raw =
        safeJsonParse(safeStorageGet(sessionStorage, ctxKey()), null) ||
        safeJsonParse(safeStorageGet(localStorage, backupKey()), null);

      if (!raw || typeof raw !== 'object') return 'ERR_CTX_MISSING';

      const ctxShop = typeof raw.shop === 'string' ? raw.shop.trim() : '';
      if (shop && ctxShop && ctxShop !== shop) return 'ERR_CTX_SHOP_MISMATCH';
      if (Number(raw.expires_at || 0) <= Date.now()) return 'ERR_CTX_EXPIRED';
      if (!raw.shop_id) return 'ERR_SHOP_ID_NULL';

      return 'ERR_CTX_MISSING';
    },

    async post(payload) {
      const url =
        typeof endpoint === 'function' ? endpoint(payload.type) : endpoint;

      let res;
      try {
        res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(nonce && payload.type === 'request'
              ? { 'X-WP-Nonce': nonce }
              : {}),
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
      } catch (networkError) {
        const err = new Error('Network error');
        err.code = 'ERR_NETWORK';
        throw err;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(
          `REST failed: ${res.status} ${res.statusText}`
        );
        err.status = res.status;
        err.serverMessage = String(body?.message || '');
        err.code = this.mapHttpErrorCode(res.status, err.serverMessage);
        throw err;
      }
      return res.json();
    },

    // 진단용 QR 컨텍스트 스냅샷 (원인 구분: 만료 vs 세션증발 vs prefix)
    buildContextSnapshot() {
      let srcParam = '';
      try {
        srcParam = new URLSearchParams(location.search).get('src') || '';
      } catch {}

      const sessionRaw = safeJsonParse(
        safeStorageGet(sessionStorage, ctxKey()),
        null
      );
      const backupRaw = safeJsonParse(
        safeStorageGet(localStorage, backupKey()),
        null
      );
      const cookieRaw = safeJsonParse(readCookie(backupKey()), null);
      const ctx = sessionRaw || backupRaw || cookieRaw || {};
      const enteredAt = Number(ctx?.entered_at || 0);
      const expiresAt = Number(ctx?.expires_at || 0);

      return {
        has_session: !!sessionRaw,
        has_backup: !!backupRaw,
        has_cookie: !!cookieRaw,
        src_param: srcParam,
        dom_src: document.body?.dataset?.qrshopSrc || '',
        ctx_shop: typeof ctx?.shop === 'string' ? ctx.shop : '',
        // loc 미해석 원인 확정용: 저장된 ctx의 실제 loc/src 값과
        // /qr/ 진입 당시 브라우저가 받은 쿼리 원문(&amp; 손상 등 판별).
        ctx_loc: typeof ctx?.loc === 'string' ? ctx.loc : '',
        ctx_src: typeof ctx?.src === 'string' ? ctx.src : '',
        entry_query:
          typeof ctx?.entry_query === 'string' ? ctx.entry_query : '',
        entered_at: enteredAt || 0,
        expires_at: expiresAt || 0,
        age_ms: enteredAt ? Date.now() - enteredAt : 0,
        prefix: getPrefix(),
      };
    },

    // 전송 실패를 관리자에게 리포트 (세션당 같은 코드 1회)
    reportError(code, type, err) {
      try {
        if (!window.__qrErrReported) window.__qrErrReported = new Set();
        if (window.__qrErrReported.has(code)) return;
        window.__qrErrReported.add(code);

        const reportEndpoint =
          window.frontendAjaxObject?.reportErrorEndpoint ||
          '/wp-json/dcraft/v1/report-error';

        const payload = JSON.stringify({
          code,
          type: type || '',
          shop: getCurrentShop(),
          loc: document.body?.dataset?.qrshopLoc || '',
          url: location.href,
          ua: navigator.userAgent,
          status: Number(err?.status || 0),
          detail: String(err?.serverMessage || err?.message || '').slice(0, 300),
          ...this.buildContextSnapshot(),
        });

        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            reportEndpoint,
            new Blob([payload], { type: 'application/json' })
          );
        } else {
          fetch(reportEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {}
    },

    async submit(type, extra = {}) {
      if (this.submitting) return false;
      this.submitting = true;

      if (type === 'request' || type === 'call' || type === 'cart') {
        if (!this.validate(type, extra)) return;
      }

      try {
        const payload = this.buildPayload(type, extra);

        // cart/call은 shop_id가 없으면 서버가 반드시 거부하므로,
        // 서버 왕복 전에 컨텍스트 유실 원인을 구체 코드로 판별한다.
        if (
          (type === 'cart' || type === 'call') &&
          !payload.meta?.shop_id
        ) {
          const err = new Error('QR context missing');
          err.code = this.diagnoseContext();
          throw err;
        }

        const result = await this.post(payload);

        this.buttonTextIng();
        if (type === 'cart' || type === 'call') {
          // 실제 접수는 직원 확인 시점이므로 "전송" 문구를 쓰고,
          // 접수 확인은 acceptStatus 폴링 배너가 담당한다.
          window.Alpine?.store('toast')?.show?.(
            '요청이 전송되었습니다.',
            'success'
          );
          window.Alpine?.store('acceptStatus')?.begin?.({
            commentId: result?.comment_id,
            token: result?.status_token,
            type,
          });
        } else {
          window.Alpine?.store('toast')?.show?.('접수되었습니다.', 'success');
        }
        window.Alpine?.store('modal')?.close?.();
        if (type === 'cart') {
          setTimeout(() => {
            window.Alpine?.store('cart')?.clear?.();
          }, 100);
        }
        return result;
      } catch (e) {
        const code = e?.code || 'ERR_UNKNOWN';
        window.Alpine?.store('toast')?.show?.(
          `전송에 실패했습니다! (${code})`,
          'error',
          4000
        );
        this.reportError(code, type, e);
        return false;
      } finally {
        this.submitting = false;
      }
    },

    // 페이지 로드 시 "최근 QR 진입인데 loc를 못 읽는" 경우를 계측(관찰 전용).
    // 순수 직접방문(비-QR) 노이즈를 막기 위해, 이 매장의 만료 전 컨텍스트가
    // 실제로 남아있을 때만 리포트한다.
    probeQrLocOnLoad() {
      try {
        const shop = getCurrentShop();
        if (!shop) return;

        // loc은 장바구니/직원호출 제출에만 쓰인다. 두 기능이 모두 꺼진
        // 매장에서는 loc 미해석이 정상이므로 계측 대상에서 제외한다.
        const useCart = window.QRSHOP_BASE?.useCart === true;
        const useCall = window.QRSHOP_BASE?.useCall === true;
        if (!useCart && !useCall) return;

        const sessionRaw = safeJsonParse(
          safeStorageGet(sessionStorage, ctxKey()),
          null
        );
        const backupRaw = safeJsonParse(
          safeStorageGet(localStorage, backupKey()),
          null
        );

        // 이 매장의 만료 전(=최근) QR 진입 흔적이 있는지
        const isRecentEntry = (c) =>
          c &&
          typeof c === 'object' &&
          (!c.shop || c.shop === shop) &&
          Number(c.expires_at || 0) > Date.now();

        const recent = isRecentEntry(sessionRaw) || isRecentEntry(backupRaw);
        if (!recent) return; // 최근 QR 진입 근거 없음 → 스킵(노이즈 방지)

        // 입력값 없이 DOM/컨텍스트만으로 loc 해석 시도
        if (this.getBaseLoc('')) return; // 정상적으로 잡히면 리포트 불필요

        this.reportError('ERR_LOC_MISSING_ON_LOAD', 'load', {
          message: 'recent QR entry but loc unresolved on load',
        });
      } catch {}
    },
  };
}
